import { OrgComponentProps } from '../../typescriptHelpers';
import * as React from 'react';
import { tryUpdateOrgSettings, UpdateMergeAccountsSettingRequest } from '../../actions/orgSettings';
import RemoveUserButton from '../RemoveUserButton';
import UpdateUserModal from '../UpdateUserModal';
import InviteTeamContainer from '../inviteTeam/InviteTeamContainer';
import MergeUsersByDomainSetting from './MergeUsersByDomainSetting';
import {
    displayTextForAppPlatform,
    displayTextForPermission,
    displayTextForUserGroupSingular,
    displayUserNameNoEmail,
} from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Table, { ColumnsType } from 'antd/lib/table';
import Popover from 'antd/lib/popover';
import { Content } from 'antd/lib/layout/layout';
import { useDispatch, useSelector } from 'react-redux';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectOrg,
    selectUserAppDataByAppIdMap,
    selectDisabledFeatureData,
    selectOrgGroups,
    selectOrgProviders,
} from '../../selectors/selectors';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { SettingOutlined, TeamOutlined } from '@ant-design/icons';
import { OrgUser, SubscriptionFeatureType, UserPermissions, UserGroupId, IntegrationProviderType } from '../../types';
import UserSearchComponent from './UserSearchComponent';
import { selectSearchedUsers } from '../../selectors/memoizedSelectors';
import { getAvailableAppsFromAppDataMap } from '../../helpers';
import AddNewUsersToAppsSetting from './AddNewUsersToAppsSetting';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Divider from 'antd/lib/divider';
import EnableACLSetting from './EnableACLSetting';
import { FilterableKeysAndOptions, FilterTableButton } from '../tableFilters/FilterTableButton';
import GroupsTable from './GroupsTable';
import Tooltip from 'antd/lib/tooltip';
import { isNil } from 'lodash';
import { SlackGroupsMappingsTable } from './SlackGroupsMappingsTable';

const OrgSettingsTeam: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const disableEditingGroups = org.ssoSamlConfig.ssoDirectory?.isActive;
    const disableEditingApps = org.ssoSamlConfig.isUsingDirectorySyncDefinedApps;
    const [changeUserGroupModalVisible, setChangeUserGroupModalVisible] = React.useState(false);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [popoversVisibility, setPopoversVisibility] = React.useState({});

    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));

    const dispatch = useDispatch();

    const allowedDomains = useSelector((state: RootState) => selectOrg(state, props.orgId).allowedDomains);
    const user = useSelector((state: RootState) => state.user);
    const searchedUsers = useSelector((state: RootState) => selectSearchedUsers(state, props.orgId));
    const [filteredUsers, setFilteredUsers] = React.useState<OrgUser[]>(searchedUsers);
    const orgIsWriteUserRestricted = useSelector((state: RootState) => {
        const disabledOrLimitedFeature = selectDisabledFeatureData(
            state,
            props.orgId,
            SubscriptionFeatureType.WriteUsers,
        );
        return !isNil(disabledOrLimitedFeature?.limit);
    });
    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const orgProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));
    const slackProviders = orgProviders.filter((provider) => provider.type === IntegrationProviderType.Slack);

    const renderTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    //hide popovers after user actions
    const handlePopoversVisibility = () => {
        setPopoversVisibility({});
    };

    const handleUserGroupModal = () => {
        handlePopoversVisibility();
        setChangeUserGroupModalVisible(true);
    };

    const actionContents = () => {
        return (
            <div style={{ width: 168 }}>
                <PermissionedActionButton
                    type="link"
                    icon={<TeamOutlined style={{ color: antGrey.grey7 }} />}
                    onClick={handleUserGroupModal}
                    style={{ color: antGrey.grey10, display: 'flex', alignItems: 'center' }}
                    actionType={RunwayUserAction.UAEditUserGroups}
                >
                    Edit user
                </PermissionedActionButton>
                <RemoveUserButton
                    selectedUser={selectedUser}
                    user={user}
                    orgId={props.orgId}
                    isDirectorySyncEnabled={org.ssoSamlConfig?.ssoDirectory?.isActive ?? false}
                    onUserRemoved={handlePopoversVisibility}
                />
            </div>
        );
    };

    const renderActions = (user: OrgUser, index: number) => {
        return (
            <Popover
                overlayClassName="org-settings-popover" //some overrides in index.less for component's inner elements
                overlayStyle={{ paddingBottom: '0px' }}
                trigger="click"
                open={popoversVisibility[index] ?? false}
                onOpenChange={(open) => {
                    const updatedPopoverVisibility = popoversVisibility;
                    updatedPopoverVisibility[index] = open;

                    setPopoversVisibility(updatedPopoverVisibility);

                    open ? setSelectedUser(user) : setSelectedUser(null);
                }}
                placement="topRight"
                content={actionContents}
                getPopupContainer={(trigger) => trigger.parentElement}
                destroyTooltipOnHide={true}
            >
                <SettingOutlined style={{ fontSize: '17px', paddingInlineEnd: '30px', color: antGrey.grey8 }} />
            </Popover>
        );
    };

    const columns: ColumnsType = [
        {
            title: renderTitle('Name'),
            width: 200,
            render: (value: OrgUser) => {
                const userName = displayUserNameNoEmail(value);
                if (!userName) {
                    return <Text type="secondary">No name</Text>;
                }
                return <Text key={value.id}>{userName}</Text>;
            },
        },
        {
            title: renderTitle('Email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: renderTitle('Permissions'),

            render: (user: OrgUser) => {
                if (!user.permissions || user.permissions.length === 0) {
                    if (orgIsWriteUserRestricted) {
                        return <Tag style={{ ...tagStyle, color: antGrey.grey7 }}>None (read-only)</Tag>;
                    }

                    return <Text type="secondary">None</Text>;
                }

                return (
                    <Space wrap>
                        {user.permissions.map((permission, index) => {
                            const permissionText =
                                orgIsWriteUserRestricted && permission === UserPermissions.Admin ?
                                    'Admin (read-write)'
                                :   displayTextForPermission(permission);

                            return (
                                <Tag key={index} style={{ ...tagStyle, color: runwayColors.primary }}>
                                    {permissionText}
                                </Tag>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: renderTitle('User groups'),
            width: 350,

            render: (user: OrgUser) => {
                const allGroups = user.groups
                    .map((groupId) => displayTextForUserGroupSingular(orgGroupsById[groupId]))
                    .join(', ');

                if (!user.groups || user.groups.length === 0) {
                    return <Text type="secondary">No groups</Text>;
                }

                return <Text>{allGroups}</Text>;
            },
        },
        {
            title: renderTitle('Apps'),

            render: (user: OrgUser) => {
                const availableApps = getAvailableAppsFromAppDataMap({
                    appDataMap: appDataByAppIdMap,
                    appIds: user.appIds,
                });

                if (!availableApps || availableApps.length === 0) {
                    return <Text type="secondary">No apps</Text>;
                }

                const appsToDisplay = availableApps.slice(0, 3);
                const remainingApps = availableApps.slice(3);
                const remainingAppsCountText = remainingApps.length == 1 ? 'other' : 'others';

                return (
                    <>
                        {appsToDisplay.map((appData, index) => {
                            return (
                                <span key={appData.appId}>
                                    {appData.appName}{' '}
                                    <Text type="secondary">
                                        ({displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                    </Text>
                                    {index !== appsToDisplay.length - 1 && ', '}
                                </span>
                            );
                        })}
                        {remainingApps.length > 0 && (
                            <Tooltip
                                title={
                                    <div>
                                        {remainingApps.map((appData, _) => (
                                            <div key={appData.appId}>
                                                {appData.appName} (
                                                {displayTextForAppPlatform(appData.platform, appData.appStoreType)})
                                            </div>
                                        ))}
                                    </div>
                                }
                            >
                                <span>
                                    , and {remainingApps.length} {remainingAppsCountText}
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            },
        },
        {
            title: '',
            align: 'right',
            render: (user: OrgUser, _, index) => {
                return renderActions(user, index);
            },
        },
    ];
    const onUpdateMergeAccountsSetting = (checked: boolean) => {
        const request: UpdateMergeAccountsSettingRequest = {
            orgId: props.orgId,
            mergeAccountsWithAllowedDomainsEnabled: checked,
        };

        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.MergeAccountsSetting, request));
    };

    const showMergeAccountsSetting = allowedDomains.length > 0;

    const filterableKeysAndOptions = React.useMemo(() => {
        const options: FilterableKeysAndOptions<OrgUser> = {
            permissions: {
                displayName: 'Permissions',
                displayNameForPossibleValue: (value) => {
                    return displayTextForPermission(value as UserPermissions);
                },
                defaultValues: [UserPermissions.Admin, UserPermissions.ITAdmin],
            },
            groups: {
                displayName: 'User groups',
                displayNameForPossibleValue: (value: UserGroupId) => {
                    return displayTextForUserGroupSingular(orgGroupsById[value]);
                },
                defaultValues: Object.keys(orgGroupsById),
                showAsDropdown: true,
            },
            appIds: {
                displayName: 'Apps',
                displayNameForPossibleValue: (value: string) => {
                    const appData = appDataByAppIdMap[value];
                    if (!appData) return 'Unknown';
                    return `${appData.appName} ${
                        appData.platform ? `(${displayTextForAppPlatform(appData.platform, appData.appStoreType)})` : ''
                    }`;
                },
                defaultValues: Object.keys(appDataByAppIdMap),
                extractPossibleValueOrValuesFromItem: (item) => {
                    //remove apps that are not in the appDataByAppIdMap
                    const appIds = item.appIds?.filter((appId) => appDataByAppIdMap[appId]);
                    return appIds ?? []; //return empty array if appIds is falsy
                },
                showAsDropdown: true,
            },
        };
        return options;
    }, [appDataByAppIdMap]);

    return (
        <Content>
            <div
                style={{
                    maxWidth: 900,
                    width: '100%',
                }}
            >
                <AddNewUsersToAppsSetting orgId={props.orgId} />
                {showMergeAccountsSetting && (
                    <>
                        <Divider />
                        <MergeUsersByDomainSetting
                            orgId={props.orgId}
                            onUpdateMergeAccountsSetting={onUpdateMergeAccountsSetting}
                        />
                    </>
                )}
                <Divider />
                <EnableACLSetting orgId={props.orgId} />
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                    marginTop: 30,
                }}
            >
                <Text
                    style={{
                        fontSize: 20,
                        color: antGrey.grey7,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {'Users'}
                </Text>
                <div
                    style={{
                        flex: 1,
                        height: '100%',
                    }}
                >
                    <Divider
                        type="horizontal"
                        style={{
                            width: '100%',
                            margin: 0,
                        }}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}>
                <UserSearchComponent />
                <Space
                    size={1}
                    style={{
                        flex: 1,
                        padding: '0 1rem',
                    }}
                >
                    <FilterTableButton<OrgUser>
                        filterableKeysAndOptions={filterableKeysAndOptions}
                        onChange={(filteredUsers) => {
                            setFilteredUsers(filteredUsers);
                        }}
                        items={searchedUsers}
                    />
                </Space>
                <InviteTeamContainer orgId={props.orgId} />
            </div>
            <Table
                dataSource={filteredUsers.map((user) => {
                    return { ...user, key: user.id };
                })}
                columns={columns}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                style={{ marginTop: 24, marginBottom: 24 }}
            />
            <UpdateUserModal
                open={changeUserGroupModalVisible}
                appDataByAppIdMap={appDataByAppIdMap}
                onCancel={() => setChangeUserGroupModalVisible(false)}
                onSuccess={() => setChangeUserGroupModalVisible(false)}
                currentUser={selectedUser}
                orgId={props.orgId}
                disableEditingGroups={disableEditingGroups}
                disableEditingApps={disableEditingApps}
                usePermissionsOnGroupLevel={org.usePermissionsOnGroupLevel}
            />
            <GroupsTable orgId={props.orgId} />
            {slackProviders.map((provider) => (
                <SlackGroupsMappingsTable
                    orgId={props.orgId}
                    providerId={provider.id}
                    slackTeamName={provider.data?.slackTeam}
                    key={provider.id}
                />
            ))}
        </Content>
    );
};

export default OrgSettingsTeam;
